import {
  Grid,
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  IconButton,
} from "@material-ui/core";
import { useState } from "react";
import Axios from "axios";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

import { EditorCkMain } from "../../components/CkEditor";
import { useAdminStyles } from "../../components/admin/adminStyles";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { useToast } from "../../components/shared/Toast";

import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

export const AddArticle = () => {
  const classes = useAdminStyles();
  const navigate = useNavigate();
  const toast = useToast();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [articles, setArticles] = useState([]);
  const [err, setErr] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState("");
  const [status, setStatus] = useState(1);

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const newImages = [];
    for (let i = 0; i < e.target.files.length; i++) {
      const file = e.target.files[i];
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: only JPEG, PNG or WebP allowed.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: must be smaller than 5 MB.`);
        continue;
      }
      newImages.push({ raw: file, url: URL.createObjectURL(file) });
    }
    if (newImages.length > 0) {
      setArticles((prev) => [...prev, ...newImages]);
    }
  };

  const removeImage = (url) => {
    setArticles((prev) => prev.filter((item) => item.url !== url));
  };

  const onSubmit = () => {
    setErr(0);
    if (!title.trim()) {
      setErr(1);
      toast.error("Please enter the article title.");
      return;
    }
    if (!date) {
      setErr(2);
      toast.error("Please select a published date.");
      return;
    }
    if (articles.length === 0) {
      setErr(3);
      toast.error("Please select at least one article image.");
      return;
    }
    if (!content || !content.trim()) {
      setErr(4);
      toast.error("Please enter the article content.");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    articles.forEach((a) => formData.append("article_images", a.raw));
    formData.append("article_title", title.trim());
    formData.append("article_desc", content);
    formData.append("article_published_date", date);
    formData.append("article_status", status);

    Axios.post("/api/add_article", formData)
      .then((res) => {
        setSubmitting(false);
        if (res.data.status) {
          toast.success(res.data.msg || "Article added");
          navigate("/dash/allarticles");
        } else {
          toast.error(res.data.msg || "Something went wrong. Try again.");
        }
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Network error. Please try again.");
      });
  };

  return (
    <div>
      <Helmet>
        <title>Add Article - Admin | Arawinz Soft Solutions</title>
      </Helmet>

      <AdminPageHeader
        eyebrow="Articles"
        title="Add new article"
        subtitle="Publish a new article or news post."
        backTo="/dash/allarticles"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Title"
            variant="outlined"
            error={err === 1}
            value={title}
            size="small"
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Published date"
            type="datetime-local"
            variant="outlined"
            value={date}
            size="small"
            error={err === 2}
            onChange={(e) => setDate(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Status"
            variant="outlined"
            value={status}
            size="small"
            onChange={(e) => setStatus(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={2}>Inactive</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Article images</Typography>
          <input
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleImageChange}
            id="article-images-upload"
            type="file"
          />
          <label htmlFor="article-images-upload" style={{ display: "block" }}>
            <Box className={classes.uploadBox}>
              <Box className={classes.uploadIconWrap}>
                <CloudUploadOutlinedIcon />
              </Box>
              <Box className={classes.uploadInfo}>
                <span>
                  <strong>Upload article images</strong>
                </span>
                <Typography className={classes.uploadHint}>
                  JPEG, PNG or WebP, up to 5 MB each. You can select multiple.
                </Typography>
              </Box>
            </Box>
          </label>
          {err === 3 && (
            <Typography style={{ color: "#dc2626", fontSize: "0.78rem", marginTop: 8 }}>
              Please select at least one image.
            </Typography>
          )}
          {articles.length > 0 && (
            <Grid container spacing={2} style={{ marginTop: 10 }}>
              {articles.map((article, idx) => (
                <Grid item xs={6} sm={4} md={3} key={article.url + idx}>
                  <Box
                    style={{
                      position: "relative",
                      borderRadius: 10,
                      overflow: "hidden",
                      border: "1px solid rgba(76,0,76,0.08)",
                    }}
                  >
                    <img
                      src={article.url}
                      alt={`upload-${idx}`}
                      style={{
                        width: "100%",
                        height: 140,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <IconButton
                      onClick={() => removeImage(article.url)}
                      size="small"
                      aria-label="Remove image"
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        background: "rgba(255,255,255,0.95)",
                        color: "#dc2626",
                        border: "1px solid rgba(220,38,38,0.25)",
                      }}
                    >
                      <DeleteOutlineIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Content</Typography>
          <EditorCkMain desc={content} setDescr={(e) => setContent(e)} />
        </Grid>
      </Grid>

      <Box className={classes.formFooter}>
        <Button
          className={classes.secondaryBtn}
          onClick={() => navigate("/dash/allarticles")}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className={classes.primaryBtn}
          endIcon={!submitting && <ArrowForwardIcon />}
        >
          {submitting ? "Adding..." : "Add Article"}
        </Button>
      </Box>
    </div>
  );
};
